import React from 'react';
import { Box, Typography } from '@mui/material';
import type { PravidlaVyjezdu } from '../api/JsdhNotifikaceService/generated/models/DeviceToken';

interface VyjezdSemaforProps {
    pocetJedu: number;
    pocetNejedu: number;
    pocetVD: number;
    pocetS: number;
    pocetH: number;
    pravidla?: PravidlaVyjezdu;
    orientation?: 'vertical' | 'horizontal';
}

type PlombaStav =
    | { typ: 'KOMPLETNI' }
    | { typ: 'CHYBI_VELITEL' }
    | { typ: 'CHYBI_STROJNIK' }
    | { typ: 'MALO_LIDI'; pocet: number; min: number }
    | null;

const evalPlomba = (
    pocetJedu: number,
    pocetVD: number,
    pocetS: number,
    pravidla?: PravidlaVyjezdu
): PlombaStav => {
    if (!pravidla?.enabled) return null;
    // Priorita: VELITEL > STROJNÍK > MÁLO LIDÍ > KOMPLETNÍ
    if (pravidla.vyzadovatVelitele && pocetVD < 1) return { typ: 'CHYBI_VELITEL' };
    if (pravidla.vyzadovatStrojnika && pocetS < 1) return { typ: 'CHYBI_STROJNIK' };
    if (pocetJedu < pravidla.minPocet) return { typ: 'MALO_LIDI', pocet: pocetJedu, min: pravidla.minPocet };
    return { typ: 'KOMPLETNI' };
};

export const VyjezdSemafor: React.FC<VyjezdSemaforProps> = ({
    pocetJedu, pocetNejedu, pocetVD, pocetS, pocetH, pravidla, orientation = 'vertical',
}) => {
    const plomba = evalPlomba(pocetJedu, pocetVD, pocetS, pravidla);
    const showCheckmarks = pravidla?.enabled === true;
    const horizontal = orientation === 'horizontal';

    // Horizontální mód (zachován původní vzhled — nekompaktní, JEDU/NEJEDU side-by-side)
    if (horizontal) {
        const jeduBoxH = (
            <Box sx={{ borderRadius: 1.5, bgcolor: 'success.main', color: 'white', textAlign: 'center', py: 0.75, px: 0.5, flex: 1 }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1 }}>JEDU</Typography>
                <Typography sx={{ fontSize: '2.6rem', fontWeight: 900, lineHeight: 1, mt: 0.25 }}>{pocetJedu}</Typography>
            </Box>
        );
        const nejeduBoxH = (
            <Box sx={{ borderRadius: 1.5, bgcolor: 'error.main', color: 'white', textAlign: 'center', py: 0.75, px: 0.5, flex: 1 }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1 }}>NEJEDU</Typography>
                <Typography sx={{ fontSize: '2.6rem', fontWeight: 900, lineHeight: 1, mt: 0.25 }}>{pocetNejedu}</Typography>
            </Box>
        );
        const plombaH = plomba && (
            <Box sx={{
                textAlign: 'center', py: 0.5, px: 1, borderRadius: 1, fontWeight: 700, fontSize: '0.95rem',
                bgcolor: plomba.typ === 'KOMPLETNI' ? 'success.main' : 'warning.main', color: 'white',
            }}>
                {plomba.typ === 'KOMPLETNI' && 'KOMPLETNÍ ✓'}
                {plomba.typ === 'CHYBI_VELITEL' && 'CHYBÍ VELITEL'}
                {plomba.typ === 'CHYBI_STROJNIK' && 'CHYBÍ STROJNÍK'}
                {plomba.typ === 'MALO_LIDI' && `MÁLO LIDÍ (${plomba.pocet}/${plomba.min})`}
            </Box>
        );
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.5, width: '100%', p: 0.5 }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>{jeduBoxH}{nejeduBoxH}</Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', alignItems: 'center', gap: 0.5, fontSize: '1rem', fontWeight: 700 }}>
                    <Box>Velitel: {pocetVD}{showCheckmarks && pravidla?.vyzadovatVelitele && (pocetVD >= 1 ? ' ✓' : ' ⚠')}</Box>
                    <Box>Strojník: {pocetS}{showCheckmarks && pravidla?.vyzadovatStrojnika && (pocetS >= 1 ? ' ✓' : ' ⚠')}</Box>
                    <Box>Hasič: {pocetH}</Box>
                </Box>
                {plombaH}
            </Box>
        );
    }

    // Vertikální mód — 3 jednolité sekce (žádné mezery, lipnou na hrany karty):
    //   - JEDU (zelená)        — pouze label + číslo
    //   - Role (neutrální)     — Velitel/Strojník/Hasič jako jedna entita, žádné gapy
    //   - NEJEDU (červená)     — pouze label + číslo
    const renderRoleRow = (
        label: string,
        count: number,
        required: boolean | undefined,
        isFirst: boolean
    ) => {
        const fail = required && count < 1;
        const ok = required && count >= 1;
        return (
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                pl: 0.5, pr: 0.5, py: 0.5,
                borderTop: isFirst ? 'none' : '1px solid',
                borderColor: 'divider',
                bgcolor: fail ? 'warning.light' : undefined,
            }}>
                <Typography sx={{
                    fontSize: '1.1rem', fontWeight: 700, lineHeight: 1,
                    color: fail ? 'warning.dark' : 'text.primary',
                }}>
                    {label}
                </Typography>
                <Typography sx={{
                    fontSize: '1.6rem', fontWeight: 900, lineHeight: 1,
                    color: fail ? 'warning.dark' : 'text.primary',
                }}>
                    {count}
                </Typography>
            </Box>
        );
    };

    return (
        <Box sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'stretch',
            // Fixní šířka — bez plomby i s plombou stejně úzká.
            // Plomba se musí vejít do této šířky (případně přes break-word a wrap).
            width: 95,
            height: '100%',
        }}>
            {/* Plomba (volitelná) — flush k hornímu kraji karty, nad JEDU sekcí.
                Wrap dovolen, font zmenšen, slova se mohou zalomit aby šířka semaforu zůstala fixní. */}
            {plomba && (
                <Box sx={{
                    textAlign: 'center', py: 0.5, px: 0.5, fontWeight: 700, fontSize: '0.75rem',
                    lineHeight: 1.15,
                    whiteSpace: 'normal', wordBreak: 'break-word',
                    bgcolor: plomba.typ === 'KOMPLETNI' ? 'success.dark' : 'warning.main',
                    color: 'white',
                }}>
                    {plomba.typ === 'KOMPLETNI' && 'KOMPLETNÍ'}
                    {plomba.typ === 'CHYBI_VELITEL' && 'CHYBÍ VELITEL'}
                    {plomba.typ === 'CHYBI_STROJNIK' && 'CHYBÍ STROJNÍK'}
                    {plomba.typ === 'MALO_LIDI' && `MÁLO LIDÍ ${plomba.pocet}/${plomba.min}`}
                </Box>
            )}

            {/* Zelená sekce: JEDU label + číslo, vyplní volné místo nahoře, vystředěno H i V */}
            <Box sx={{
                flex: 1,
                bgcolor: 'success.main', color: 'white',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                py: 1, px: 0.5,
            }}>
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, lineHeight: 1 }}>JEDU</Typography>
                <Typography sx={{ fontSize: '2.8rem', fontWeight: 900, lineHeight: 1, mt: 0.4 }}>{pocetJedu}</Typography>
            </Box>

            {/* Role sekce: Velitel + Strojník + Hasič — kompaktní pásek mezi JEDU a NEJEDU (jen výška obsahu) */}
            <Box sx={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', bgcolor: 'action.hover' }}>
                {renderRoleRow('Velitel', pocetVD, showCheckmarks && pravidla?.vyzadovatVelitele, true)}
                {renderRoleRow('Strojník', pocetS, showCheckmarks && pravidla?.vyzadovatStrojnika, false)}
                {renderRoleRow('Hasič', pocetH, false, false)}
            </Box>

            {/* Červená sekce: NEJEDU label + číslo, vyplní volné místo dole, vystředěno H i V */}
            <Box sx={{
                flex: 1,
                bgcolor: 'error.main', color: 'white',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                py: 1, px: 0.5,
            }}>
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, lineHeight: 1 }}>NEJEDU</Typography>
                <Typography sx={{ fontSize: '2.8rem', fontWeight: 900, lineHeight: 1, mt: 0.4 }}>{pocetNejedu}</Typography>
            </Box>
        </Box>
    );
};
